import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import {
  CheckCircle2,
  Zap,
  Crown,
  Calendar,
  Monitor,
  ShieldCheck,
  BadgeCheck,
  Lock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const PricingSection = ({ packages: propPackages }) => {
  const [currency, setCurrency] = useState('USD');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const { data: fetchedPackages } = useQuery({
    queryKey: ['publicPricing'],
    queryFn: () => axiosClient.get('/public/pricing'),
    enabled: !propPackages || propPackages.length === 0,
  });

  const packages = propPackages || fetchedPackages || [];

  if (!packages || packages.length === 0) {
    return (
      <div className="py-24 text-center text-slate-500 font-mono">
        Scanning for available subscription nodes...
      </div>
    );
  }

  const getPrice = (pkg) => {
    const priceObj = pkg.pricing?.find((p) => p.currency === currency);
    return priceObj ? priceObj.amount : 0;
  };

  return (
    <div className="py-20 md:py-28 bg-[#0b0f19] relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-400/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-flex rounded-full border border-teal-500/20 bg-teal-500/10 px-4 py-2 text-[11px] font-black uppercase tracking-[0.22em] text-teal-400">
            AIRS Protection Plans
          </span>

          <h2 className="mt-6 text-4xl md:text-5xl font-black text-white tracking-tight">
            Choose the security layer your infrastructure needs.
          </h2>

          <p className="mt-5 text-slate-400 text-lg leading-relaxed">
            Transparent licensing for autonomous monitoring, vulnerability protection, and AI-assisted response.
          </p>

          <div className="flex justify-center mt-8">
            <div className="rounded-2xl border border-white/10 bg-slate-950/70 p-1.5 flex shadow-2xl">
              <button
                onClick={() => setCurrency('USD')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  currency === 'USD'
                    ? 'bg-white text-slate-950 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                USD ($)
              </button>

              <button
                onClick={() => setCurrency('INR')}
                className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  currency === 'INR'
                    ? 'bg-white text-slate-950 shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                INR (₹)
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-8 items-stretch">
          {packages.map((pkg, index) => {
            const isPopular = index === 1 || pkg.name.toLowerCase().includes('pro');
            const symbol = currency === 'INR' ? '₹' : '$';
            const price = getPrice(pkg);

            let features = [];
            try {
              features =
                typeof pkg.features_list === 'string'
                  ? JSON.parse(pkg.features_list)
                  : pkg.features_list || [];
            } catch (e) {
              features = Array.isArray(pkg.features_list) ? pkg.features_list : [];
            }

            return (
              <div
                key={pkg.id}
                className={`relative flex flex-col rounded-[2rem] border p-6 md:p-7 overflow-hidden shadow-2xl transition-all duration-300 group hover:-translate-y-1.5 ${
                  isPopular
                    ? 'bg-slate-950/90 border-blue-500/50 shadow-blue-500/20 md:scale-[1.03]'
                    : 'bg-slate-950/65 border-white/10 hover:border-white/20'
                }`}
              >
                <div
                  className={`absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl transition-opacity ${
                    isPopular ? 'bg-blue-500/20 opacity-80' : 'bg-white/[0.05] opacity-60 group-hover:opacity-90'
                  }`}
                />

                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

                {isPopular && (
                  <div className="absolute top-5 right-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-lg flex items-center">
                    <Crown className="w-3 h-3 mr-1" /> Most Popular
                  </div>
                )}

                <div className="relative">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className={`h-13 w-13 rounded-2xl border flex items-center justify-center ${
                        isPopular
                          ? 'border-blue-500/30 bg-blue-500/10 text-blue-400'
                          : 'border-teal-500/20 bg-teal-500/10 text-teal-400'
                      }`}
                    >
                      {isPopular ? <BadgeCheck className="w-6 h-6" /> : <ShieldCheck className="w-6 h-6" />}
                    </div>

                    <div>
                      <h3 className="text-xl font-black text-white leading-tight">{pkg.name}</h3>
                      <p className="mt-1 text-[10px] font-black uppercase tracking-[0.18em] text-slate-600">
                        AIRS License Plan
                      </p>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-white/10 bg-white/[0.035] p-5 mb-6">
                    <div className="flex items-end gap-2 flex-wrap">
                      <span className="text-4xl md:text-5xl font-black text-white tracking-tight break-all">
                        {symbol}
                        {price}
                      </span>
                      <span className="text-slate-500 font-bold text-sm mb-1">
                        / {pkg.billing_cycle?.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-7">
                    <div
                      className={`p-3 rounded-2xl border ${
                        isPopular ? 'bg-blue-500/10 border-blue-500/30' : 'bg-white/[0.035] border-white/10'
                      }`}
                    >
                      <div className="flex items-center mb-1">
                        <Calendar className={`w-3.5 h-3.5 mr-1.5 ${isPopular ? 'text-blue-400' : 'text-slate-400'}`} />
                        <span className="text-[10px] uppercase font-black text-slate-500 tracking-wider">
                          Validity
                        </span>
                      </div>
                      <span className="text-white font-bold text-sm">{pkg.duration_days} Days</span>
                    </div>

                    <div
                      className={`p-3 rounded-2xl border ${
                        isPopular ? 'bg-purple-500/10 border-purple-500/30' : 'bg-white/[0.035] border-white/10'
                      }`}
                    >
                      <div className="flex items-center mb-1">
                        <Monitor className={`w-3.5 h-3.5 mr-1.5 ${isPopular ? 'text-purple-400' : 'text-slate-400'}`} />
                        <span className="text-[10px] uppercase font-black text-slate-500 tracking-wider">
                          License
                        </span>
                      </div>
                      <span className="text-white font-bold text-sm">{pkg.max_devices} Device</span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-4 mb-8 pt-6 border-t border-white/10">
                    {features &&
                      features.map((feat, i) => (
                        <div key={i} className="flex items-start text-sm text-slate-300">
                          <CheckCircle2
                            className={`w-5 h-5 mr-3 flex-shrink-0 ${
                              isPopular ? 'text-blue-400' : 'text-teal-500'
                            }`}
                          />
                          <span className="leading-relaxed">{String(feat).trim().replace(/['"]+/g, '')}</span>
                        </div>
                      ))}
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate(`/portal/shop?plan=${pkg.id}`);
                    } else {
                      navigate(`/register?plan=${pkg.id}`);
                    }
                  }}
                  className={`relative w-full py-4 rounded-2xl font-black transition-all flex items-center justify-center group-hover:scale-[1.02] ${
                    isPopular
                      ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                      : 'bg-white/[0.055] hover:bg-white/[0.09] text-white border border-white/10'
                  }`}
                >
                  Choose Plan <Zap className="w-4 h-4 ml-2 fill-current" />
                </button>

                <div className="relative mt-5 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-600">
                  <Lock className="w-3.5 h-3.5" />
                  Secure checkout
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingSection;